import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { ProtocolVersion, ProtocolVersionForm, ProtocolVersionSearchForm } from 'entities/protocol_version'

type ProtocolVersionsResponse = PagingResponse & {
    results: ProtocolVersion[]
}

export function useFetchProtocolVersionsApi(searchForm: Form<ProtocolVersionSearchForm>): IndexApiSet<ProtocolVersionsResponse> & { execute: () => void } {
    const apiPath = 'protocol_versions/'
    const api = useIndexApi<ProtocolVersionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

export function usePostProtocolVersionApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProtocolVersionForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ProtocolVersionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProtocolVersionForm>) => {
        const apiPath = `protocol_versions/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutProtocolVersionApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProtocolVersionForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ProtocolVersionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProtocolVersionForm>) => {
        const apiPath = `protocol_versions/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutProtocolVersionsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProtocolVersionForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ProtocolVersionForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProtocolVersionForm[]>) => {
        const apiPath = `protocol_versions/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteProtocolVersionApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `protocol_versions/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}